import { Formik } from "formik";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useFormContext } from "../../context/FormContext";
import useSurveyForm from "../../hooks/useSurveyForm";
import { validateV2, getFields } from "../../utils";
import SurveyFormContent from "../Form/SurveyFormContent";
import { getInitialValues3 } from "../../utils/functions/getInitialValues";
import { makeSchemaV3 } from "../../utils/functions/makeBuildSchema";
import { removeLabelCharacter } from "../../utils/functions/getName";
import { getTrustedOriginSuffix } from "../../utils/functions";
import Footer from "../Footer";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useToast } from "../../hooks/useToast";

const EMPTY_ARRAY = [];

const SurveyContainer = () => {
  const {
    name,
    token,
    form_fields,
    merchant,
    description,
    id: webFormId,
    initails_email_response,
    draft,
  } = useFormContext();

  const {
    submitData,
    submitError,
    submitSurvey,
    loadDraft,
    isDraftLoading,
  } = useSurveyForm();

  const [initialValues, setInitialValues] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const { show: showToast } = useToast();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const merchantName = searchParams.get("merchantName") || "";
  const accessToken = searchParams.get("accessToken") || "";

  // Memoize transformedFields to prevent unnecessary recalculations
  const transformedFields = useMemo(() => {
    const fieldsToTransform = form_fields || EMPTY_ARRAY;
    
    const labelCounts = {};
    const typeCounts = {};

    const getUniqueLabel = (label) => {
      if (!label) return "";
      const clean = removeLabelCharacter(label);
      if (labelCounts[clean] !== undefined) {
        labelCounts[clean] += 1;
        return `${clean}_${labelCounts[clean]}`;
      }
      labelCounts[clean] = 0;
      return clean;
    };

    const getUniqueType = (type) => {
      if (!type) return "";
      const clean = removeLabelCharacter(type);
      if (typeCounts[clean] !== undefined) {
        typeCounts[clean] += 1;
        return `${clean}_${typeCounts[clean]}`;
      }
      typeCounts[clean] = 0;
      return clean;
    };

    return fieldsToTransform.map((item) => ({
      ...item,
      displayLabel: item.field_label,
      field_label: getUniqueLabel(item.field_key || item.field_label),
      field_key: item.field_label,
      field_type: getUniqueType(item.field_type),
    }));
  }, [form_fields]);

  // Memoize schema
  const schema = useMemo(() => {
    const buildSchema = makeSchemaV3(transformedFields);
    return buildSchema();
  }, [transformedFields]);

  // ====================== LOAD INITIAL VALUES (Optimized) ======================
  useEffect(() => {
    let isMounted = true;

    const loadInitialValues = async () => {
      if (!transformedFields || transformedFields.length === 0) return;

      let initial = {};

      // Priority 1: Draft from API
      if (draft?.exists && draft?.values) {

        transformedFields.forEach((field) => {
          const finalKey = field.field_label;
          const originalKey = field.field_key || field.field_label;

          let draftValue = draft.values
            .find((item) => item.name === finalKey || item.name === originalKey);

            if (!draftValue) {
              draftValue = draft.values 
                .find((item) => item.label === finalKey || item.label === originalKey)
            }

          let value = draftValue?.value || draftValue?.values;

          // Fallback: fuzzy match by cleaning label
          if (value === undefined) {
            const cleanOriginal = removeLabelCharacter(originalKey).trim();
            for (const [key, val] of Object.entries(draft.values)) {
              if (removeLabelCharacter(key).trim() === cleanOriginal) {
                value = val;
                break;
              }
            }
          }

          initial[finalKey] = value !== undefined ? value : (field.field_type?.includes("checkbox") ? [] : "");
        });

        if (isMounted) {
          setInitialValues(initial);
        }

        return;
      }

      // Priority 2: Local draft
      const localDraft = await loadDraft(token);
      if (localDraft && isMounted) {
        setInitialValues(localDraft);
        return;
      }

      // Priority 3: Fresh form
      if (isMounted) {
        const base = getInitialValues3(transformedFields);

        transformedFields.forEach((field) => {
          if (
            field.field_type?.includes("checkbox") &&
            field.field_items?.every((item) => !isNaN(Number(item)))
          ) {
            base[field.field_label] = null;
          }
        });

        setInitialValues(base);
      }
    };

    if (name) {
      loadInitialValues();
    }

    return () => {
      isMounted = false;
    };
  }, [name, draft, transformedFields, token, loadDraft]);

  // ====================== SUBMIT ======================
  const onSubmit = useCallback(async (values, { setSubmitting }) => {
    try {
      const submissionValues = {
        email: userEmail || searchParams.get("email") || "",
        summary: description || name,
        description: name,
        web_form_id: webFormId,
        form_share_link: window.location.href,
        is_submitted: values?.is_submitted ?? true,
        fields: getFields(values)?.fields || values,
        channel: "capture",
        merchant_name: merchantName,
        initails_email_response: initails_email_response || "",
      };

      await submitSurvey({
        token,
        accessToken,
        type: "capture",
        data: submissionValues,
        is_submitted: values?.is_submitted ?? true,
        formikOptions: { setSubmitting },
        merchantData: merchant,
        files: [],
      });
      if (values?.is_submitted === false) {
        navigate("/survey-success", {
          state: {
            type: "draft",
            merchant,
            merchant_name: merchantName
          }
        });
      }
    } catch (error) {
      console.error("Submit error:", error);
      showToast({
        title: "Error",
        description: "An unexpected error occurred",
        color: "error"
      });
    }
  }, [userEmail, description, name, webFormId, merchantName, initails_email_response, token, accessToken, merchant, submitSurvey, searchParams, showToast]);

  // ====================== CONTINUE LATER ======================
  const handleContinueLater = useCallback((data) => {
    onSubmit({ ...data.values, is_submitted: false }, data.formik || { setSubmitting: () => {} });
  }, [onSubmit]);

  // ====================== MESSAGE HANDLER ======================
  const handleMessage = useCallback((event) => {
    const { data: eventData, origin } = event;
    const trustedSuffix = getTrustedOriginSuffix();

    const isTrusted = origin === window.location.origin || origin.endsWith(trustedSuffix);

    if (!isTrusted || !eventData || eventData.target !== "survey") return;

    if (eventData.type === "USER_EMAIL" && eventData.email) {
      setUserEmail(eventData.email);
      localStorage.setItem("survey_user_email", eventData.email);
      if (window.parent !== window) {
        window.parent.postMessage({ type: "ACK", target: "parent" }, origin);
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  // Update ticket ID
  useEffect(() => {
    if (submitData?.data?.is_submitted) {
      navigate("/survey-success", {
        state: {
          type: "submit",
        }
      });
    }
  }, [submitData, navigate]);

  useEffect(() => {
    if (submitError) {
      showToast({
        title: "Submission Error",
        description: submitError,
        color: "error"
      });
    }
  }, [submitError, showToast]);

  return (
    <div className="survey-container">
      <div className="survey-card">
        <div className="survey-header">
          <h1>{name}</h1>
          <hr className="header-divider" />
        </div>

        <div className="survey-content">
          {initialValues ? (
            <Formik
              key={name}
              initialValues={initialValues}
              enableReinitialize={true}
              validate={(values) => validateV2(values, schema)}
              onSubmit={onSubmit}
            >
              <SurveyFormContent
                fields={transformedFields}
                saveDraft={handleContinueLater}
                token={token}
                isDraftLoading={isDraftLoading}
              />
            </Formik>
          ) : (
            <div className="loading-initials">Preparing survey...</div>
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default SurveyContainer;
